import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import { categoryHex, categoryLabel } from "../lib/categories";
import { formatCurrency } from "../lib/utils-image";

// Donut chart of expenses by category + RTL legend with amounts and percentages.
export const CategoryPie = ({ data = [], currency = "SAR", title, subtitle, testid = "category-pie" }) => {
  const total = data.reduce((s, d) => s + (d.total || 0), 0);

  return (
    <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6" data-testid={testid}>
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-1 font-readex">المصاريف حسب الصنف</div>
        <h2 className="font-readex font-medium text-xl text-[#1A1C19]">{title}</h2>
        {subtitle && <p className="text-xs text-[#8A8D86] mt-1">{subtitle}</p>}
      </div>

      {total <= 0 ? (
        <div className="py-10 text-center text-[#8A8D86]">
          <PieIcon className="w-9 h-9 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm font-readex">لا توجد مصاريف في هذه الفترة</p>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative w-full sm:w-1/2 h-56 shrink-0" data-testid={`${testid}-chart`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="total"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="90%"
                  paddingAngle={2}
                  strokeWidth={0}
                  isAnimationActive
                >
                  {data.map((d) => (
                    <Cell key={d.category} fill={categoryHex(d.category)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name) => [formatCurrency(v, currency), categoryLabel(name)]}
                  contentStyle={{ direction: "rtl", fontFamily: "inherit", borderRadius: 8, border: "1px solid rgba(26,28,25,0.1)" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[10px] text-[#8A8D86] font-readex">الإجمالي</div>
              <div className="font-mono-num font-semibold text-[#1A1C19] text-sm sm:text-base">
                {formatCurrency(total, currency)}
              </div>
            </div>
          </div>

          <ul className="w-full sm:flex-1 space-y-2" data-testid={`${testid}-legend`}>
            {data.map((d) => {
              const pct = total > 0 ? Math.round((d.total / total) * 100) : 0;
              return (
                <li key={d.category} className="flex items-center gap-2.5 text-sm">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: categoryHex(d.category) }} />
                  <span className="font-readex text-[#1A1C19] truncate flex-1">{categoryLabel(d.category)}</span>
                  <span className="font-mono-num text-[#5C605A] whitespace-nowrap">{formatCurrency(d.total, currency)}</span>
                  <span className="font-mono-num text-xs text-[#8A8D86] w-10 text-end">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
